if (!customElements.get('xoa-cart-incentive')) {
  customElements.define(
    'xoa-cart-incentive',
    class XoaCartIncentive extends HTMLElement {
      connectedCallback() {
        this.message = this.querySelector('[data-incentive-message]');
        this.action = this.querySelector('[data-incentive-action]');
        this.closeButton = this.querySelector('[data-incentive-close]');
        this.cartIcon = document.getElementById('cart-icon-bubble');
        this.threshold = Number.parseInt(this.dataset.threshold, 10);
        this.discount = Number.parseInt(this.dataset.discount, 10) || 0;
        this.reward = this.dataset.reward;
        this.currency = this.dataset.currency;
        this.offerSignature = this.dataset.offerSignature || '';
        this.storageKey = 'xoa-cart-incentive-dismissed';
        this.storageItemCountKey = `${this.storageKey}-item-count`;
        this.dismissed = this.getDismissedSignature() === this.offerSignature;
        this.itemCount = Number.parseInt(this.dataset.itemCount, 10) || 0;
        const dismissedItemCount = this.getDismissedItemCount();
        if (this.dismissed && dismissedItemCount !== null && this.itemCount > dismissedItemCount) {
          this.restore();
        }
        this.requestSequence = 0;

        this.handleClose = () => this.dismiss();
        this.handleKeydown = (event) => {
          if (event.key === 'Escape' && !this.hidden) this.dismiss();
        };
        this.closeButton?.addEventListener('click', this.handleClose);
        document.addEventListener('keydown', this.handleKeydown);

        this.render(
          Number.parseInt(this.dataset.subtotal, 10) || 0,
          this.currency,
          this.itemCount
        );

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.handleCartUpdate(event));
        }

        this.handlePageShow = (event) => event.persisted && this.fetchCart();
        this.handleVisibilityChange = () => !document.hidden && this.fetchCart();
        window.addEventListener('pageshow', this.handlePageShow);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.abortController?.abort();
        this.closeButton?.removeEventListener('click', this.handleClose);
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('pageshow', this.handlePageShow);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      }

      handleCartUpdate(event) {
        const cart = event?.cartData;
        if (Number.isFinite(cart?.items_subtotal_price) && Number.isFinite(cart?.item_count)) {
          this.requestSequence += 1;
          this.abortController?.abort();
          this.render(cart.items_subtotal_price, cart.currency || this.currency, cart.item_count);
          return;
        }

        this.fetchCart();
      }

      async fetchCart() {
        const sequence = ++this.requestSequence;
        this.abortController?.abort();
        this.abortController = new AbortController();

        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart.js`, {
            headers: { Accept: 'application/json' },
            signal: this.abortController.signal,
          });

          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
          const cart = await response.json();
          if (sequence === this.requestSequence) {
            this.render(cart.items_subtotal_price, cart.currency || this.currency, cart.item_count);
          }
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        }
      }

      render(subtotal, currency, itemCount) {
        if (!Number.isFinite(this.threshold) || this.threshold <= 0) return;

        const normalizedItemCount = Math.max(0, Number(itemCount) || 0);
        if (normalizedItemCount > this.itemCount && this.dismissed) this.restore();
        this.itemCount = normalizedItemCount;
        this.syncVisibility(normalizedItemCount);

        const normalizedSubtotal = Math.max(0, Number(subtotal) || 0);
        const complete = normalizedSubtotal >= this.threshold;
        const remaining = Math.max(0, this.threshold - normalizedSubtotal);
        const template = this.resolveTemplate(complete);
        const amount = this.formatMoney(remaining, currency);

        this.currency = currency;
        this.renderMessage(template, amount, String(this.discount));
        this.classList.toggle('is-complete', complete);
        this.toggleAttribute('data-complete', complete);

        if (this.action) this.action.hidden = !complete;
      }

      renderMessage(template, amount, discount) {
        const parts = String(template || '').split(/(\[amount\]|\[discount\])/g);
        const fragment = document.createDocumentFragment();

        parts.forEach((part) => {
          if (part === '[amount]') {
            const emphasis = document.createElement('strong');
            emphasis.className = 'xoa-cart-incentive__amount';
            emphasis.textContent = amount;
            fragment.append(emphasis);
            return;
          }

          fragment.append(document.createTextNode(part === '[discount]' ? discount : part));
        });

        this.message.replaceChildren(fragment);
      }

      syncVisibility(itemCount) {
        const hasItems = Number(itemCount) > 0;
        this.hidden = !hasItems || this.dismissed;
      }

      dismiss() {
        this.dismissed = true;
        this.hidden = true;

        try {
          window.sessionStorage.setItem(this.storageKey, this.offerSignature);
          window.sessionStorage.setItem(this.storageItemCountKey, String(this.itemCount));
        } catch (_error) {
          // The notice can still be dismissed for the current page if storage is unavailable.
        }

        this.cartIcon?.focus();
      }

      restore() {
        this.dismissed = false;

        try {
          window.sessionStorage.removeItem(this.storageKey);
          window.sessionStorage.removeItem(this.storageItemCountKey);
        } catch (_error) {
          // The notice can still reopen on the current page if storage is unavailable.
        }
      }

      getDismissedSignature() {
        try {
          return window.sessionStorage.getItem(this.storageKey);
        } catch (_error) {
          return null;
        }
      }

      getDismissedItemCount() {
        try {
          const value = window.sessionStorage.getItem(this.storageItemCountKey);
          if (value === null) return null;

          const itemCount = Number.parseInt(value, 10);
          return Number.isFinite(itemCount) ? itemCount : null;
        } catch (_error) {
          return null;
        }
      }

      resolveTemplate(complete) {
        if (this.reward === 'discount') {
          return complete ? this.dataset.discountComplete : this.dataset.discountProgress;
        }

        if (this.reward === 'free_gift') {
          return complete ? this.dataset.giftComplete : this.dataset.giftProgress;
        }

        return complete ? this.dataset.shippingComplete : this.dataset.shippingProgress;
      }

      formatMoney(cents, currency) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'fi', {
            style: 'currency',
            currency: currency || 'EUR',
            minimumFractionDigits: 0,
          }).format(cents / 100);
        } catch (_error) {
          return `${(cents / 100).toFixed(2)} ${currency || 'EUR'}`;
        }
      }

    }
  );
}
